/**
 * Navigation of the Lehrkraft-Bereich and the Bewerbungs-Bereich (order and texts as in the mockup): the items of the Vanilla
 * navigation drawer (…_SIDE_NAV, with „Hilfe & Weiterbildung“, see portalDrawerItems) and on phones additionally the bottom
 * navigation (TEACHER_NAV / APPLICANT_NAV).
 */
export type PortalNavItem = {
    /** Matches route meta `portalTab` */
    tab: string;
    text: string;
    to: string;
    icon: string;
    activeIcon: string;
    /**
     * Menu key (NavigationDrawerAccess, e.g. 'PAYOFF') the login needs for this page, through roles or personal guiAccess;
     * without it the tab is hidden and the route redirects to „Heute“ (see visiblePortalNav / portalTabAllowed)
     */
    guiAccess?: string;
};

export const TEACHER_NAV: PortalNavItem[] = [
    {tab: 'today', text: 'Heute', to: '/my-teaching/today', icon: 'mdi-home-outline', activeIcon: 'mdi-home'},
    {tab: 'schedule', text: 'Stundenplan', to: '/my-teaching/schedule', icon: 'mdi-calendar-blank-outline', activeIcon: 'mdi-calendar'},
    {tab: 'substitutions', text: 'Vertretungen', to: '/my-teaching/substitutions', icon: 'mdi-account-switch-outline', activeIcon: 'mdi-account-switch'},
    {tab: 'payoff', text: 'Abrechnung', to: '/my-teaching/payoff', icon: 'mdi-receipt-text-outline', activeIcon: 'mdi-receipt-text',
        guiAccess: 'PAYOFF'},
    {tab: 'profile', text: 'Profil', to: '/my-teaching/profile', icon: 'mdi-account-outline', activeIcon: 'mdi-account'},
];

export const APPLICANT_NAV: PortalNavItem[] = [
    {tab: 'application', text: 'Bewerbung', to: '/my-application', icon: 'mdi-file-account-outline', activeIcon: 'mdi-file-account'},
    {tab: 'profile', text: 'Profil', to: '/my-application/profile', icon: 'mdi-account-outline', activeIcon: 'mdi-account'},
    {tab: 'trainings', text: 'Schulungen', to: '/my-application/trainings', icon: 'mdi-school-outline', activeIcon: 'mdi-school'},
    {tab: 'contact', text: 'Kontakt', to: '/my-application/contact', icon: 'mdi-phone-outline', activeIcon: 'mdi-phone'},
];

/** „Verfügbarkeiten“: only in the menu (between Stundenplan and Vertretungen), on phones via Profil */
export const TEACHER_AVAILABILITIES_NAV: PortalNavItem = {
    tab: 'availabilities', text: 'Verfügbarkeiten', to: '/my-teaching/availabilities', icon: 'mdi-clock-outline', activeIcon: 'mdi-clock',
};

/** „Kursprotokolle“ (the quick logging view of the old Heute): in the menu right after Stundenplan, on phones via Heute/Stundenplan */
export const TEACHER_COURSE_LOGS_NAV: PortalNavItem = {
    tab: 'courseLogs', text: 'Kursprotokolle', to: '/my-teaching/course-logs', icon: 'mdi-notebook-edit-outline', activeIcon: 'mdi-notebook-edit',
};

/**
 * „Coachingzone“ (external page with a shared password, in the office in the footer – the portal shows no footer): only in the
 * menu of both areas (last item), on phones via Heute (Lehrkraft) / Schulungen (Bewerbung) – the bottom navigation stays as it is.
 * No menu key: like the office footer, every login of the area sees it (no server data behind it).
 */
export const COACHING_ZONE_TAB = 'coachingZone';

export function coachingZoneNav(portal: 'teacher' | 'applicant'): PortalNavItem {
    return {
        tab: COACHING_ZONE_TAB, text: 'Coachingzone', to: portal === 'teacher' ? '/my-teaching/coachingzone' : '/my-application/coachingzone',
        icon: 'mdi-music-circle-outline', activeIcon: 'mdi-music-circle',
    };
}

/** Menu (Vanilla navigation drawer) of the Lehrkraft-Bereich: bottom navigation + Kursprotokolle, Verfügbarkeiten, Kündigungen, Hilfe & Weiterbildung, Coachingzone */
export const TEACHER_SIDE_NAV: PortalNavItem[] = [
    ...TEACHER_NAV.slice(0, 2),
    TEACHER_COURSE_LOGS_NAV,
    TEACHER_AVAILABILITIES_NAV,
    ...TEACHER_NAV.slice(2),
    {tab: 'terminations', text: 'Kündigungen', to: '/my-teaching/terminations', icon: 'mdi-account-cancel-outline', activeIcon: 'mdi-account-cancel'},
    {tab: 'help', text: 'Hilfe & Weiterbildung', to: '/my-teaching/help', icon: 'mdi-lifebuoy', activeIcon: 'mdi-lifebuoy'},
    coachingZoneNav('teacher'),
];

/** Menu of the Bewerbungs-Bereich: bottom navigation + Coachingzone */
export const APPLICANT_SIDE_NAV: PortalNavItem[] = [...APPLICANT_NAV, coachingZoneNav('applicant')];

/**
 * Items the login may see: items with `guiAccess` only with that menu key. `granted` are the effective menu keys
 * (NavigationDrawerAccess.getEffectiveAccess(store.state.guiAccess), the same rule as the office drawer and routeAccess.ts).
 */
export function visiblePortalNav(items: PortalNavItem[], granted: readonly string[]): PortalNavItem[] {
    return items.filter((item) => !item.guiAccess || granted.includes(item.guiAccess));
}

/** Route guard of the Lehrkraft-Bereich: a page whose tab (route meta portalTab) needs a menu key the login lacks is not opened */
export function portalTabAllowed(tab: string | undefined, granted: readonly string[]): boolean {
    const item = TEACHER_SIDE_NAV.find((navItem) => navItem.tab === tab);
    return !item?.guiAccess || granted.includes(item.guiAccess);
}

/**
 * Active tab of the bottom navigation: pages without an own tab belong to „Heute“ (Hilfe, Coachingzone), „Stundenplan“
 * (Kursprotokolle) or „Profil“ (Verfügbarkeiten); in the Bewerbungs-Bereich the Coachingzone belongs to „Schulungen“
 */
export function bottomNavTab(tab: string, portal: 'teacher' | 'applicant' = 'teacher'): string {
    if (tab === COACHING_ZONE_TAB) {
        return portal === 'applicant' ? 'trainings' : 'today';
    }
    if (tab === 'availabilities') {
        return 'profile';
    }
    if (tab === 'courseLogs') {
        return 'schedule';
    }
    return tab === 'help' ? 'today' : tab;
}

export type PortalDrawerItem = { type: 'item'; text: string; icon: string; to: string };

/**
 * Items of the Vanilla navigation drawer in the portal: the portal pages (same drawer design as the office);
 * office logins with the teacher role additionally get „Zur Büro-Ansicht“.
 */
export function portalDrawerItems(portal: 'teacher' | 'applicant', items: PortalNavItem[], withOfficeLink: boolean): PortalDrawerItem[] {
    const out: PortalDrawerItem[] = items.map((item) => ({type: 'item', text: item.text, icon: item.icon, to: item.to}));
    if (withOfficeLink && portal === 'teacher') {
        out.push({type: 'item', text: 'Zur Büro-Ansicht', icon: 'mdi-office-building-outline', to: '/'});
    }
    return out;
}
